import type { TStateAidReceivedQuarterFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import {
  MONTH_OPTIONS,
  QUARTER_OPTIONS,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';

export type TStateAidQuarterWindowItem = Pick<
  TStateAidReceivedQuarterFormValues,
  'year' | 'quarter'
>;

export const parseStateAidQuarter = (value?: string): number | null => {
  const normalized = value?.trim().toLowerCase().replace(/\s+/g, ' ');

  if (!normalized) return null;

  const match = normalized.match(
    /^(?:q\s*([1-4])|([1-4])\s*q|quarter\s*:?\s*([1-4])|([1-4]|i{1,3}|iv)\s*квартал)$/i
  );
  if (!match) return null;

  const token = match.slice(1).find(Boolean)?.toUpperCase();
  const quarter = QUARTER_OPTIONS.find(({ label }) => label.startsWith(`${token} квартал`));

  return quarter ? Number(quarter.value.replace('quarter:', '')) : Number(token);
};

const parseStateAidMonth = (value?: string): number | null => {
  const normalized = value?.trim().toLowerCase().replace(/\s+/g, ' ');

  if (!normalized) return null;

  const monthMatch = normalized.match(/^month\s*:?\s*(1[0-2]|[1-9])$/);
  if (monthMatch?.[1]) return Number(monthMatch[1]);

  const month = MONTH_OPTIONS.find(({ label }) => label.toLowerCase() === normalized);

  return month ? Number(month.value.replace('month:', '')) : null;
};

export const getStateAidQuarterWindow = (
  yearPeriod?: string,
  childPeriod?: string
): TStateAidQuarterWindowItem[] | null => {
  const endYear = Number(yearPeriod);
  const endQuarter = parseStateAidQuarter(childPeriod);
  const month = parseStateAidMonth(childPeriod);

  if (!Number.isInteger(endYear) || (!endQuarter && !month)) return null;

  const endAbsoluteQuarter = endQuarter
    ? endYear * 4 + endQuarter - 1
    : endYear * 4 + Math.floor((month! - 1) / 3) - 1;

  return Array.from({ length: 12 }, (_, index) => {
    const absoluteQuarter = endAbsoluteQuarter - (11 - index);

    return {
      year: Math.floor(absoluteQuarter / 4),
      quarter: (absoluteQuarter % 4) + 1,
    };
  });
};

export const getStateAidQuarterLabel = ({ year, quarter }: TStateAidQuarterWindowItem): string =>
  `${QUARTER_OPTIONS[quarter - 1]?.label ?? ''} ${year}`;
